import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';

/**
 * Cart — full-screen drawer that slides up.
 * Shows all cart items with quantity controls, total, clear all,
 * and "Order via WhatsApp" button.
 */
export default function Cart({ restaurant, onClose }) {
  const { cart, cartTotal, removeFromCart, addToCart, clearCart, loc, t } = useApp();
  const [closing, setClosing] = useState(false);

  const symbol = restaurant?.currencySymbol || 'DH';

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleClose = () => {
    setClosing(true);
    setTimeout(onClose, 250);
  };

  // Build WhatsApp order message
  const buildWhatsAppMessage = () => {
    const rName = loc(restaurant.name);
    let msg = `🍽️ *${t('orderSummary')} — ${rName}*\n\n`;
    cart.forEach((item) => {
      msg += `• ${loc(item.name)} × ${item.quantity} — ${item.price * item.quantity} ${symbol}\n`;
    });
    msg += `\n💰 *${t('total')}: ${cartTotal} ${symbol}*`;
    const encoded = encodeURIComponent(msg);
    return `https://wa.me/${restaurant.whatsapp?.replace(/\+/g, '')}?text=${encoded}`;
  };

  return (
    <div
      style={{
        ...styles.overlay,
        animation: closing ? 'fadeIn 0.25s ease reverse forwards' : 'fadeIn 0.25s ease',
      }}
      onClick={handleClose}
    >
      <div
        style={{
          ...styles.sheet,
          animation: closing ? 'slideUp 0.25s ease reverse forwards' : 'slideUp 0.3s ease',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.handleBar}>
            <div style={styles.handle} />
          </div>
          <div style={styles.headerRow}>
            <h2 style={styles.title}>🛒 {t('cart')}</h2>
            <button style={styles.closeBtn} onClick={handleClose}>
              ✕
            </button>
          </div>
        </div>

        {/* Cart content */}
        <div style={styles.body}>
          {cart.length === 0 ? (
            <div style={styles.empty}>
              <div style={{ fontSize: '3.5rem', marginBottom: '1rem' }}>🛒</div>
              <h3 style={styles.emptyTitle}>{t('emptyCart')}</h3>
              <p style={styles.emptyDesc}>{t('emptyCartDesc')}</p>
              <button style={styles.browseBtn} onClick={handleClose}>
                {t('browseMenu')} →
              </button>
            </div>
          ) : (
            <>
              {/* Clear all */}
              <div style={styles.clearRow}>
                <span style={styles.itemCount}>
                  {cart.reduce((s, i) => s + i.quantity, 0)} {t('quantity').toLowerCase()}
                </span>
                <button style={styles.clearBtn} onClick={clearCart}>
                  {t('clearCart')}
                </button>
              </div>

              {/* Cart items */}
              {cart.map((item) => (
                <div key={item.id} style={styles.cartItem}>
                  <div style={styles.cartItemImageWrap}>
                    <img
                      src={item.image}
                      alt={loc(item.name)}
                      style={styles.cartItemImage}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                  <div style={styles.cartItemInfo}>
                    <span style={styles.cartItemName}>{loc(item.name)}</span>
                    <span style={styles.cartItemPrice}>
                      {item.price * item.quantity} {symbol}
                    </span>
                  </div>
                  <div style={styles.qtyControls}>
                    <button
                      style={styles.qtyBtn}
                      onClick={() => removeFromCart(item.id)}
                    >
                      −
                    </button>
                    <span style={styles.qtyNum}>{item.quantity}</span>
                    <button
                      style={styles.qtyBtn}
                      onClick={() =>
                        addToCart({
                          id: item.id,
                          name: item.name,
                          price: item.price,
                          image: item.image,
                        })
                      }
                    >
                      +
                    </button>
                  </div>
                </div>
              ))}

              {/* Total & order */}
              <div style={styles.totalSection}>
                <div style={styles.totalRow}>
                  <span style={styles.totalLabel}>{t('total')}</span>
                  <span style={styles.totalAmount}>
                    {cartTotal} <span style={styles.totalCurrency}>{symbol}</span>
                  </span>
                </div>

                {restaurant.whatsapp && (
                  <a
                    href={buildWhatsAppMessage()}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={styles.whatsappBtn}
                  >
                    <svg style={{ width: 20, height: 20 }} viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    {t('orderWhatsApp')}
                  </a>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 300,
    backgroundColor: 'var(--bg-overlay)',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  sheet: {
    width: '100%',
    maxWidth: '480px',
    maxHeight: '85vh',
    backgroundColor: 'var(--bg-primary)',
    borderTopLeftRadius: 'var(--radius-xl)',
    borderTopRightRadius: 'var(--radius-xl)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    padding: '0.5rem 1.25rem 0.75rem',
    borderBottom: '1px solid var(--border-light)',
  },
  handleBar: {
    display: 'flex',
    justifyContent: 'center',
    paddingBottom: '0.75rem',
  },
  handle: {
    width: '36px',
    height: '4px',
    borderRadius: '2px',
    backgroundColor: 'var(--border)',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontFamily: 'var(--font-display)',
    fontSize: '1.25rem',
    fontWeight: 700,
  },
  closeBtn: {
    width: '32px',
    height: '32px',
    borderRadius: 'var(--radius-full)',
    backgroundColor: 'var(--bg-tertiary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.85rem',
    color: 'var(--text-secondary)',
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: '1rem 1.25rem',
  },
  empty: {
    textAlign: 'center',
    padding: '3rem 1rem',
  },
  emptyTitle: {
    fontFamily: 'var(--font-display)',
    fontSize: '1.2rem',
    fontWeight: 600,
    marginBottom: '0.5rem',
  },
  emptyDesc: {
    fontSize: '0.85rem',
    color: 'var(--text-muted)',
    marginBottom: '1.5rem',
  },
  browseBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: 'var(--accent)',
    color: 'var(--text-inverse)',
    fontWeight: 600,
    borderRadius: 'var(--radius-full)',
    fontSize: '0.9rem',
  },
  clearRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '1rem',
  },
  itemCount: {
    fontSize: '0.82rem',
    color: 'var(--text-muted)',
    fontWeight: 500,
  },
  clearBtn: {
    fontSize: '0.78rem',
    color: 'var(--danger)',
    fontWeight: 500,
  },
  cartItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.75rem 0',
    borderBottom: '1px solid var(--border-light)',
  },
  cartItemImageWrap: {
    width: '50px',
    height: '50px',
    borderRadius: 'var(--radius-sm)',
    overflow: 'hidden',
    flexShrink: 0,
    backgroundColor: 'var(--bg-tertiary)',
  },
  cartItemImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  cartItemInfo: {
    flex: 1,
    minWidth: 0,
  },
  cartItemName: {
    display: 'block',
    fontSize: '0.9rem',
    fontWeight: 600,
    color: 'var(--text-primary)',
    marginBottom: '0.15rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  cartItemPrice: {
    fontSize: '0.82rem',
    color: 'var(--accent)',
    fontWeight: 600,
  },
  qtyControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    flexShrink: 0,
  },
  qtyBtn: {
    width: '30px',
    height: '30px',
    borderRadius: 'var(--radius-full)',
    border: '1.5px solid var(--border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1rem',
    fontWeight: 600,
    color: 'var(--text-primary)',
    backgroundColor: 'var(--bg-secondary)',
  },
  qtyNum: {
    fontSize: '0.9rem',
    fontWeight: 700,
    minWidth: '20px',
    textAlign: 'center',
  },
  totalSection: {
    marginTop: '1.25rem',
    paddingTop: '1rem',
    borderTop: '2px solid var(--border)',
  },
  totalRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '1rem',
  },
  totalLabel: {
    fontSize: '1rem',
    fontWeight: 600,
    color: 'var(--text-secondary)',
  },
  totalAmount: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: 'var(--accent)',
  },
  totalCurrency: {
    fontSize: '0.85rem',
    fontWeight: 500,
  },
  whatsappBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.6rem',
    width: '100%',
    padding: '0.95rem',
    backgroundColor: '#25D366',
    color: '#ffffff',
    fontWeight: 600,
    fontSize: '1rem',
    borderRadius: 'var(--radius-lg)',
    textDecoration: 'none',
    transition: 'all var(--transition)',
    boxShadow: '0 4px 16px rgba(37, 211, 102, 0.3)',
  },
};
